import { useState } from 'react';
import { Group, Stack, Title } from '@mantine/core';
import { IconGaugeOff } from '@tabler/icons-react';
import { CsvDownloadButton } from '../components/CsvDownloadButton';
import { DateRangeFilter } from '../components/DateRangeFilter';
import { SiteFilterSelect, StageFilterSelect } from '../components/FilterSelects';
import { ReportPage } from '../components/ReportPage';
import { ReportTable, type ReportColumn } from '../components/ReportTable';
import { rangeQuery, useYieldReport } from '../api';
import { formatDate, formatKg, formatPct } from '../format';
import type { YieldByStageRow, YieldRunRow } from '../types';

/** Report #4 (BUILD_SPEC.md §9.5, §13) — per-run detail vs recipe, plus a
 * per-stage rollup weighted by input dry mass. */
export function YieldReport() {
  const [siteId, setSiteId] = useState<string | null>(null);
  const [stageId, setStageId] = useState<string | null>(null);
  const [from, setFrom] = useState<string | null>(null);
  const [to, setTo] = useState<string | null>(null);

  const query = useYieldReport({ siteId: siteId ?? undefined, stageId: stageId ?? undefined, from, to });
  const byStage = query.data?.byStage ?? [];
  const runs = query.data?.runs ?? [];

  const stageColumns: ReportColumn<YieldByStageRow>[] = [
    { key: 'stage', header: 'Stage', render: (r) => r.stageName },
    { key: 'runs', header: 'Runs', align: 'right', render: (r) => String(r.runCount) },
    { key: 'inputDryKg', header: 'Total input dry kg', align: 'right', render: (r) => formatKg(r.totalInputDryKg) },
    { key: 'avgYield', header: 'Weighted avg yield', align: 'right', render: (r) => `${r.weightedAvgYieldDryPct.toFixed(2)}%` },
  ];

  const runColumns: ReportColumn<YieldRunRow>[] = [
    { key: 'number', header: 'Run', render: (r) => r.number },
    { key: 'stage', header: 'Stage', render: (r) => r.stageName },
    { key: 'mode', header: 'Mode', render: (r) => r.runMode },
    { key: 'started', header: 'Started', render: (r) => formatDate(r.startedAt) },
    { key: 'ended', header: 'Ended', render: (r) => formatDate(r.endedAt) },
    { key: 'inputDryKg', header: 'Input dry kg', align: 'right', render: (r) => formatKg(r.inputDryKg) },
    {
      key: 'actual',
      header: 'Actual yield',
      align: 'right',
      // R2 companion contract: null means "no KG output mass to compute a yield
      // from" (e.g. a KG->EACH packing run) — explicit null check (not truthiness)
      // so a genuine 0% yield doesn't also collapse to "—".
      render: (r) => (r.yieldDryPctActual !== null ? `${Number(r.yieldDryPctActual).toFixed(2)}%` : '—'),
    },
    { key: 'expected', header: 'Expected yield', align: 'right', render: (r) => `${Number(r.expectedYieldDryPct).toFixed(2)}%` },
    { key: 'variance', header: 'Variance', align: 'right', render: (r) => formatPct(r.yieldVariancePct) },
  ];

  return (
    <ReportPage
      title="Yield by stage/run"
      description="Actual vs recipe dry-mass yield (§9.5). Per-stage figures are weighted by each run's input dry mass."
      action={
        <CsvDownloadButton
          path="/reporting/yield"
          query={{
            siteId: siteId ?? undefined,
            stageId: stageId ?? undefined,
            ...rangeQuery(from, to),
          }}
          filename="yield-by-run.csv"
          description="Exports the yield-by-stage and yield-by-run breakdowns, matching your filters, as a CSV file"
        />
      }
    >
      <Group gap="md" wrap="wrap" align="flex-end">
        <SiteFilterSelect value={siteId} onChange={setSiteId} label="Site" w={220} />
        <StageFilterSelect value={stageId} onChange={setStageId} siteId={siteId ?? undefined} label="Stage" w={220} />
        <DateRangeFilter from={from} to={to} onFromChange={setFrom} onToChange={setTo} />
      </Group>

      <Stack gap="lg">
        <Stack gap="xs">
          <Title order={4}>By stage</Title>
          <ReportTable
            columns={stageColumns}
            rows={byStage}
            getRowKey={(r) => r.stageId}
            isLoading={query.isLoading}
            isError={query.isError}
            errorMessage="Failed to load the yield report."
            emptyMessage="No posted runs for the selected filters"
            emptyHint="Try a different stage, site or date range."
            emptyIcon={IconGaugeOff}
          />
        </Stack>

        <Stack gap="xs">
          <Title order={4}>By run</Title>
          <ReportTable
            columns={runColumns}
            rows={runs}
            getRowKey={(r) => r.runId}
            isLoading={query.isLoading}
            isError={query.isError}
            errorMessage="Failed to load the yield report."
            emptyMessage="No posted runs for the selected filters"
            emptyHint="Try a different stage, site or date range."
            emptyIcon={IconGaugeOff}
          />
        </Stack>
      </Stack>
    </ReportPage>
  );
}
